import {
  AutoCompleteMemberOption,
  AutoCompleteTeamOption,
  SelectedOption,
} from "@components/common/members-autocomplete/members-autocomplete-types";
import { GROUPS_SORT_OPTIONS } from "@components/groups/groups-constants";
import { GroupsSortBy, GroupsSortField, GroupTypes } from "@custom-types/group-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ElementType } from "react";

/** Determine whether the type is for details of a group */
export function isGroupDetails(
  group: GroupTypes
): group is FaroCoreApiTypes.GroupDetailResponse {
  // Use the unique fields "members" and "projects" from the IGroupDetails for the type guard check.
  return "members" in group && "projects" in group;
}

/** Whether the project status is of type ProjectArchivingState */
export function isProjectArchivingState(
  projectStatus: ProjectArchivingState | undefined
): projectStatus is ProjectArchivingState {
  return (
    projectStatus !== undefined &&
    Object.values(ProjectArchivingState).includes(projectStatus)
  );
}

/** Determine whether the provided image src is png or svg */
export function isSrcOfTypeString(src: string | ElementType): src is string {
  return typeof src === "string";
}

/** Whether the value is in type of string[] or not */
export function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    // value.every returns true for an empty array. So it needs to be checked separately
    value.length > 0 &&
    value.every((item) => typeof item === "string")
  );
}

/** @returns Whether the value is a FileSystemDirectoryHandle. */
export function isFileSystemDirectoryHandle(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemDirectoryHandle {
  return (
    ((value as FileSystemHandle).kind === "directory" || value.isDirectory) &&
    (value as FileSystemDirectoryHandle).entries instanceof Function
  );
}

/** @returns Whether the value is a FileSystemDirectoryEntry. */
export function isFileSystemDirectoryEntry(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemDirectoryEntry {
  return (
    value.isDirectory &&
    (value as FileSystemDirectoryEntry).createReader instanceof Function
  );
}

/** @returns Whether the value is a FileSystemFileHandle. */
export function isFileSystemFileHandle(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemFileHandle {
  return (
    ((value as FileSystemHandle).kind === "file" || value.isFile) &&
    (value as FileSystemFileHandle).getFile instanceof Function
  );
}

/** @returns Whether the value is a FileSystemFileEntry. */
export function isFileSystemFileEntry(
  value: FileSystemHandle | FileSystemEntry
): value is FileSystemFileEntry {
  return (
    value.isFile && (value as FileSystemFileEntry).file instanceof Function
  );
}

/** Returns whether the provided option is new to the workspace, meaning that an email was added */
export function isOptionNew(option: SelectedOption): option is string {
  return typeof option === "string";
}

/** Returns whether the provided autocomplete option is a predefined member */
export function isOptionMember(
  option: SelectedOption
): option is AutoCompleteMemberOption {
  return typeof option !== "string" && option.type === "member";
}

/** Returns whether the provided autocomplete option is a predefined team */
export function isOptionTeam(
  option: SelectedOption
): option is AutoCompleteTeamOption {
  return typeof option !== "string" && option.type === "team";
}

/**
 * Type guard to check if a string is a valid GroupsSortField
 */
export function isGroupsSortField(field: string): field is GroupsSortField {
  return field in GROUPS_SORT_OPTIONS;
}

/**
 * Type guard to check if a value is a valid GroupsSortBy
 */
export function isGroupsSortBy(value: string | null): value is GroupsSortBy {
  if (!value) {
    return false;
  }
  return (
    value === "name_ascending" ||
    value === "name_descending" ||
    value === "members_ascending" ||
    value === "members_descending" ||
    value === "projects_ascending" ||
    value === "projects_descending" ||
    value === "createdDate_ascending" ||
    value === "createdDate_descending"
  );
}